import React, { useCallback, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import {
  CREATE,
  FunctionField,
  GET_ONE,
  Pagination,
  ReferenceManyField,
  refreshView,
  showNotification,
  TextField,
  translate as translateHOC,
  withDataProvider,
} from 'react-admin';
import { Button, Grid, Typography, withStyles } from '@material-ui/core';
import GetAppIcon from '@material-ui/icons/GetApp';
import VisibilityIcon from '@material-ui/icons/Visibility';
import withGrants from '../../WithGrants';
import Header from '../../../shared/header';
import CustomDatagrid from '../../../shared/custom-datagrid';
import InvoiceFilters from '../../invoice/list/invoiceFilters';
import RefundByItemDialog from '../../refund-by-item/RefundByItemDialog';
import RefundNoteViewerDialog from '../../refund-by-item/RefundNoteViewerDialog';
import StatusChip from '../../../shared/status-chip';
import { statusInfos } from '../../../core/data-fields/data-invoices';
import { getAlternateStatus, getInvoiceDisplayedActions } from '../../../helpers/invoiceHelpers';
import SetAsPaid from '../actions/SetAsPaid';
import { useUnitInvoiceActions } from '../useUnitInvoiceActions';
import { calculateTimeDifferenceInDays } from '../../../shared/utils/date';
import CurrencyField from '../../custom-fields/CurrencyField';
import TimezoneAwareDateField from '../../custom-fields/TimezoneAwareDateField';

const styles = ({ spacing: { unit } }) => ({
  actions: {
    gap: `${unit}px`,
    padding: `${unit}px 0`,
  },
  actionButton: {
    height: 'fit-content',
  },
  icon: {
    marginRight: 8,
    fontSize: 20,
  },
});

const Actions = withStyles(styles)(({ classes, translate, invoice, actions, isAdmin, isBusinessOwner }) => {
  return (
    <Grid container spacing={8} className={classes.actions}>
      {actions.map(({ action, label, callback, icon }) => {
        if (
          !getInvoiceDisplayedActions({
            action,
            amount: invoice.amount,
            invoiceId: invoice.id,
            pspName: invoice.pspName,
            invoiceStatus: getAlternateStatus({
              isPaidExternally: invoice.isPaidExternally,
              mainStatus: invoice.invoicesStatus,
              paymentInProgress: invoice.paymentInProgress,
              refundInProgress: invoice.refundInProgress,
            }),
            paymentInProgress: invoice.paymentInProgress,
            refundInProgress: invoice.refundInProgress,
            isAdmin,
            isBusinessOwner,
          })
        ) {
          return null;
        }

        return (
          <Button
            key={action}
            variant="outlined"
            color="primary"
            onClick={() => callback(invoice.id, invoice)}
            className={classes.actionButton}
          >
            {icon}
            {translate(label)}
          </Button>
        );
      })}
    </Grid>
  );
});

const MAX_PERIOD_IN_DAYS = 366;

const getDefaultColumns = (isAdmin, isBusinessOwner) => {
  if (isBusinessOwner || isAdmin) {
    return ['id', 'pspReference', 'creationDate', 'amount', 'type', 'status', 'actions'];
  }

  if (isAdmin === undefined || isBusinessOwner === undefined) {
    return null;
  }

  return ['id', 'pspReference', 'creationDate', 'amount', 'type', 'status'];
};

const UnitInvoicesList = props => {
  const { dataProvider, translate, record, dispatch, classes, timezone, isBusinessOwner } = props;

  const defaultFilter = useMemo(() => {
    const now = new Date();
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(now.getFullYear() - 1);
    return {
      startDate: oneYearAgo.toISOString().split('T')[0],
      endDate: now.toISOString().split('T')[0],
      timezone,
    };
  }, [timezone]);

  const role = localStorage.getItem('role');
  const isAdmin = role === 'admin';

  const [currentSelection, setCurrentSelection] = useState();
  const [filterValues, setFiltersValues] = useState(defaultFilter);

  // Dialogs states
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState({});
  // Refund-by-item state (instant/unit variant): the enriched refundableAmount DTO plus the
  // single refundable receipt (its pspReference is displayed, its paymentIntentPspReference is
  // sent on POST), held as one object so the post-fetch update is a single re-render.
  const [refundData, setRefundData] = useState({
    items: null,
    amount: 0,
    byRate: [],
    currency: 'EUR',
    receipt: null,
    loading: false,
  });
  const [isSubmittingRefund, setIsSubmittingRefund] = useState(false);
  const [refundSubmitted, setRefundSubmitted] = useState(null);
  const [refundNoteOpen, setRefundNoteOpen] = useState(false);
  const [refundNoteInvoiceId, setRefundNoteInvoiceId] = useState(null);
  const [setAsPaidDialogOpen, setSetAsPaidDialogOpen] = useState(false);
  const defaultColumns = getDefaultColumns(isAdmin, isBusinessOwner);

  const pagination = useRef();

  const getDataGridProps = useCallback(() => {
    const datagridProps = { ...props };
    ['classes', 'translate', 'downloadPdf', 'dataProvider', 'dispatch'].forEach(propName => {
      delete datagridProps[propName];
    });

    return datagridProps;
  }, [props]);

  const setFilters = (key, value) => setFiltersValues(prev => ({ ...prev, [key]: value }));

  const resetDates = () => setFiltersValues(defaultFilter);

  const { cancel, downloadInvoicePdf, loadRefundNotePdf, retryPayment, setAsPaid } = useUnitInvoiceActions({
    dataProvider,
    dispatch,
    isBusinessOwner,
  });

  const openRefundDialog = useCallback(
    async (invoiceId, invoice) => {
      setSelectedInvoice(invoice);
      setRefundSubmitted(null);
      setIsSubmittingRefund(false);
      setRefundData({
        items: null,
        amount: 0,
        byRate: [],
        currency: invoice.currency || 'EUR',
        receipt: null,
        loading: true,
      });
      setRefundDialogOpen(true);
      try {
        const { data } = await dataProvider(GET_ONE, 'businessUnitInvoiceRefundableAmount', {
          field: 'refundableAmount',
          id: invoiceId,
        });
        // One refundable receipt per Payment Intent (a PI can be refunded once); pick the PAID
        // one (else the first, defensively) for its PSP ref (display) + paymentIntentPspReference
        // (which PI to refund, sent on POST). If none, it stays null and the BE validates.
        const dto = data || {};
        const receipts = dto.refundablePaymentReceipts || [];
        setRefundData({
          items: Array.isArray(dto.refundableItems) ? dto.refundableItems : [],
          amount: dto.refundableAmount,
          byRate: dto.refundableByRate || [],
          currency: dto.currency || invoice.currency || 'EUR',
          receipt: receipts.find(r => r.status === 'PAID') || receipts[0] || null,
          loading: false,
        });
      } catch (error) {
        console.error(`Error while fetching refundable amount for invoice ${invoiceId}`, error);
        // Close on a load failure so the operator can retry, rather than dropping into the
        // empty state (which would read as "nothing to refund").
        setRefundDialogOpen(false);
        setRefundData(prev => ({ ...prev, loading: false }));
        dispatch(
          showNotification('notification.invoices.refundedFailure', 'warning', {
            messageArgs: { error_message: error },
          })
        );
      }
    },
    [dataProvider, dispatch]
  );

  const closeRefundDialog = useCallback(() => {
    setRefundDialogOpen(false);
    // Refetch after a completed submit so the row reflects the true backend status.
    if (refundSubmitted) dispatch(refreshView());
  }, [refundSubmitted, dispatch]);

  const onRefund = useCallback(
    ({ note, refundItems: items, receipt }) => {
      setIsSubmittingRefund(true);
      const paymentIntentPspReference = refundData.receipt && refundData.receipt.paymentIntentPspReference;
      dataProvider(CREATE, 'businessUnitInvoices', {
        field: 'refund',
        id: selectedInvoice.id,
        payload: { note, refundItems: items, paymentIntentPspReference },
        data: selectedInvoice,
      })
        .then(() => {
          // The POST is async (no body); refetch on close reflects the true status.
          setIsSubmittingRefund(false);
          setRefundSubmitted(receipt || {});
          dispatch(showNotification('notification.invoices.refundedSuccessfully'));
        })
        .catch(error => {
          console.error(`Failed to refund invoice ${selectedInvoice.id}`, error);
          setIsSubmittingRefund(false);
          dispatch(
            showNotification('notification.invoices.refundedFailure', 'warning', {
              messageArgs: { error_message: error },
            })
          );
        });
    },
    [dataProvider, selectedInvoice, refundData.receipt, dispatch]
  );

  const openRefundNote = useCallback(invoiceId => {
    setRefundNoteInvoiceId(invoiceId);
    setRefundNoteOpen(true);
  }, []);
  const closeRefundNote = useCallback(() => setRefundNoteOpen(false), []);
  const loadRefundNote = useCallback(() => loadRefundNotePdf(refundNoteInvoiceId), [
    loadRefundNotePdf,
    refundNoteInvoiceId,
  ]);

  const openSetAsPaidDialog = (_, invoice) => {
    setSelectedInvoice(invoice);
    setSetAsPaidDialogOpen(true);
  };
  const closeSetAsPaidDialog = () => {
    setSetAsPaidDialogOpen(false);
  };

  const timeDifferenceInDays = calculateTimeDifferenceInDays(
    new Date(filterValues.startDate),
    new Date(filterValues.endDate)
  );

  if (isAdmin === undefined || isBusinessOwner === undefined || !Array.isArray(defaultColumns)) return null;

  return (
    <>
      <InvoiceFilters
        translate={translate}
        filterValues={filterValues}
        setFilters={setFilters}
        resetDates={resetDates}
        isFilteredDatesOverLimit={timeDifferenceInDays > MAX_PERIOD_IN_DAYS}
        periodLimit={12}
      />
      <Header label="menu.unitInvoices" />
      {timeDifferenceInDays > MAX_PERIOD_IN_DAYS ? (
        <Typography variant="body2">{translate('resources.invoice.messages.invalidFilters')}</Typography>
      ) : (
        <ReferenceManyField
          {...props}
          pagination={<Pagination ref={pagination.current} rowsPerPageOptions={[10, 25, 50]} />}
          record={record}
          filter={filterValues}
          reference="businessUnitInvoices"
          target="id"
          sort={{ field: 'invoiceDate', order: 'DESC' }}
          currentSelection={currentSelection}
        >
          <CustomDatagrid
            {...getDataGridProps()}
            onColumnChange={selection => {
              setCurrentSelection(selection);
            }}
            defaultColumns={defaultColumns}
            id="Selenium-Business-Invoices-Unit-Table"
          >
            <TextField key="id" source="id" sortable={false} />
            <TextField key="pspReference" source="pspReference" sortable={false} />
            <TimezoneAwareDateField key="creationDate" source="invoiceDate" sortable />
            <CurrencyField key="amount" source="amount" sortable sortBy="amount" />
            <FunctionField
              key="type"
              source="type"
              sortable={false}
              render={() => translate('resources.business.fields.subscription')}
            />
            <FunctionField
              key="status"
              source="invoicesStatus"
              sortable
              render={ref => {
                if (ref && ref.invoicesStatus) {
                  return ref.paidExternally || (ref.pspName && ref.pspName === 'ext_wire_transfer') ? (
                    <StatusChip
                      label={translate(`resources.invoice.status.PAID_EXTERNALLY`)}
                      {...statusInfos[ref.invoicesStatus]}
                    />
                  ) : (
                    <StatusChip
                      label={translate(`resources.invoice.status.${ref.invoicesStatus}`)}
                      {...statusInfos[ref.invoicesStatus]}
                    />
                  );
                }
                return null;
              }}
            />
            <FunctionField
              key="actions"
              label="resources.invoice.fields.actions"
              sortable={false}
              render={invoice => (
                <Actions
                  translate={translate}
                  invoice={invoice}
                  actions={[
                    { action: 'pay', label: 'resources.invoice.retryPayment', callback: retryPayment },
                    {
                      action: 'externalPayment',
                      label: 'resources.invoice.setAsPaid',
                      callback: openSetAsPaidDialog,
                    },
                    { action: 'refund', label: 'resources.invoice.refund', callback: openRefundDialog },
                    {
                      action: 'cancelInvoice',
                      label: 'resources.invoice.cancel',
                      callback: cancel,
                    },
                    {
                      action: 'refundNote',
                      label: 'resources.invoice.refundNote',
                      callback: () => openRefundNote(invoice.id),
                      icon: <VisibilityIcon className={classes.icon} />,
                    },
                    {
                      action: 'invoice',
                      label: 'resources.invoice.PDF',
                      callback: () => downloadInvoicePdf(invoice.id),
                      icon: <GetAppIcon className={classes.icon} />,
                    },
                  ]}
                  isAdmin={isAdmin}
                  isBusinessOwner={isBusinessOwner}
                />
              )}
            />
          </CustomDatagrid>
        </ReferenceManyField>
      )}
      <RefundByItemDialog
        open={refundDialogOpen}
        variant="unit"
        invoiceId={selectedInvoice.id}
        invoiceLabel={selectedInvoice.pspReference || selectedInvoice.id}
        entityName={record && record.name}
        pspReference={refundData.receipt && refundData.receipt.pspReference}
        currency={refundData.currency}
        items={refundData.items}
        refundableAmount={refundData.amount}
        refundableByRate={refundData.byRate}
        loading={refundData.loading}
        submitting={isSubmittingRefund}
        submitted={refundSubmitted}
        onClose={closeRefundDialog}
        onSubmit={onRefund}
      />
      <RefundNoteViewerDialog open={refundNoteOpen} loadPdf={loadRefundNote} onClose={closeRefundNote} />
      <SetAsPaid
        open={setAsPaidDialogOpen}
        onClose={closeSetAsPaidDialog}
        invoiceId={selectedInvoice.id}
        setAsPaid={payload => setAsPaid(selectedInvoice.id, payload, selectedInvoice)}
      />
    </>
  );
};

const mapStateToProps = state => ({
  timezone: state.configuration.fleet.timezone,
  isBusinessOwner: (() => {
    const userDetails = state.currentUser.payload;
    return userDetails && userDetails.userRoles && userDetails.userRoles.some(role => role === 'BusinessOwner');
  })(),
});

UnitInvoicesList.propTypes = {
  classes: PropTypes.shape({}),
  dataProvider: PropTypes.func,
  dispatch: PropTypes.func,
  record: PropTypes.shape({}),
  timezone: PropTypes.string,
  translate: PropTypes.func,
  isBusinessOwner: PropTypes.bool,
};

export default compose(
  withGrants,
  withDataProvider,
  translateHOC,
  connect(mapStateToProps),
  withStyles(styles)
)(UnitInvoicesList);
